import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { formatCurrencyTotals, formatMoney } from "../../lib/money";

// Command Center widgets. Each widget fetches its own admin-only endpoint
// (/api/command-center/<key>) and has its own loading / error / empty state,
// so one failing endpoint never blanks the page. Money is per currency only.

export const SECTION_LINKS = [
  { to: "/crm/ai", label: "AI Studio", detail: "Today, copilot, campaigns, usage" },
  { to: "/crm/money", label: "Money", detail: "Revenue, expenses, payouts per currency" },
  { to: "/crm/email", label: "Email", detail: "Compose, inbox, sent log, privacy" },
  { to: "/crm/social", label: "Social", detail: "X queue, Instagram, post tracker" },
  { to: "/crm/settings", label: "Settings", detail: "Connections, AI, Mux, brand, roles" },
];

export const PREFS_KEY = "gp.commandCenter.widgets.v1";
const ATHENS = "Europe/Athens";
const NEON = { borderColor: "rgba(255, 16, 240, 0.28)", boxShadow: "0 0 26px -18px #FF10F0" };

function fmtWhen(value, withDate = false) {
  if (!value) return "";
  const s = String(value);
  if (s.length === 10) return withDate ? s : "All day";
  const d = new Date(s);
  if (!Number.isFinite(d.getTime())) return s;
  return d.toLocaleString("en-GB", { timeZone: ATHENS, ...(withDate ? { dateStyle: "medium" } : {}), timeStyle: "short" });
}

export function useWidgetData(path) {
  const [state, setState] = useState({ data: null, loading: true, error: "" });
  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: "" }));
    try {
      const { data } = await api.get(path);
      setState({ data, loading: false, error: "" });
    } catch (e) {
      const status = e?.response?.status;
      setState({ data: null, loading: false, error: status === 401 || status === 403 ? "Admin sign-in required" : "Could not load this widget" });
    }
  }, [path]);
  useEffect(() => { load(); }, [load]);
  return { ...state, reload: load };
}

function Empty({ children }) {
  return <p className="py-2 text-sm text-zinc-500">{children}</p>;
}

function Row({ label, value, testid, tone = "text-white" }) {
  return <div className="flex items-baseline justify-between gap-3 border-b border-zinc-800/70 py-2 last:border-0">
    <span className="text-xs text-zinc-400">{label}</span>
    <span data-testid={testid} className={`text-right text-sm font-semibold tabular-nums ${tone}`}>{value}</span>
  </div>;
}

function WidgetFrame({ id, title, link, linkLabel, state, children, controls }) {
  return <section data-testid={`cc-widget-${id}`} aria-label={title} className="flex min-w-0 flex-col rounded-xl border bg-black/60 p-4" style={NEON}>
    <header className="mb-3 flex items-center justify-between gap-2">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-[#FF10F0]">{title}</h2>
      <div className="flex items-center gap-1">
        {controls}
        {state?.reload && <button type="button" onClick={state.reload} aria-label={`Refresh ${title}`} className="rounded px-1.5 text-xs text-zinc-400 hover:text-white">↻</button>}
      </div>
    </header>
    <div className="min-w-0 flex-1">
      {state?.loading && !state?.data ? <p className="text-sm text-zinc-500" role="status">Loading…</p>
        : state?.error ? <div role="alert" className="text-sm text-red-300">{state.error}. <button type="button" className="underline" onClick={state.reload}>Retry</button></div>
          : children}
    </div>
    {link && <Link to={link} className="mt-3 inline-block text-xs font-medium text-[#FF10F0] hover:underline">{linkLabel || "Open"} →</Link>}
  </section>;
}

function pct(value) {
  if (value === null || value === undefined) return "no data last month";
  return `${value >= 0 ? "+" : ""}${value.toFixed(1)}%`;
}

function RevenueWidget({ controls }) {
  const s = useWidgetData("/api/command-center/revenue");
  const d = s.data;
  return <WidgetFrame id="revenue" title="Revenue" link="/crm/money" linkLabel="Money" state={s} controls={controls}>
    {d && <>
      <Row label="This month (ledger)" testid="cc-rev-this-month" value={formatCurrencyTotals(d.this_month, { empty: "No entries yet" })} tone="text-emerald-300" />
      <Row label="Last month" testid="cc-rev-last-month" value={formatCurrencyTotals(d.last_month, { empty: "No entries" })} />
      {Object.keys(d.change_pct || {}).length > 0 && <Row label="Change" testid="cc-rev-change" value={Object.entries(d.change_pct).map(([c, v]) => `${c} ${pct(v)}`).join(" · ")} />}
      <Row label="Platform earnings (all time)" testid="cc-rev-platforms" value={formatCurrencyTotals(d.platform_earnings_by_currency, { empty: "Not recorded" })} />
      <p className="mt-3 text-[11px] uppercase text-zinc-500">Top platforms this month</p>
      {d.top_platforms_this_month?.length ? <ul className="mt-1 space-y-1">{d.top_platforms_this_month.map((p) => <li key={`${p.platform}-${p.currency}`} className="flex justify-between text-sm"><span className="truncate text-zinc-200">{p.platform}</span><span className="tabular-nums text-emerald-300">{formatMoney(p.amount, p.currency)}</span></li>)}</ul>
        : <Empty>No revenue recorded this month.</Empty>}
      <p className="mt-2 text-[11px] text-zinc-500">Per currency, never converted or added together.</p>
    </>}
  </WidgetFrame>;
}

function ItemList({ items, empty, withDate }) {
  if (!items?.length) return <Empty>{empty}</Empty>;
  return <ul className="space-y-1">{items.map((i) => <li key={`${i.source}-${i.id}-${i.when}`} className="flex justify-between gap-2 text-sm">
    <span className="truncate text-zinc-200">{i.title}{i.platform ? <span className="ml-1 text-xs text-zinc-500">· {i.platform}</span> : null}</span>
    <span className="shrink-0 text-xs tabular-nums text-zinc-400">{fmtWhen(i.when, withDate)}</span>
  </li>)}</ul>;
}

function igNext(d) {
  if (d.instagram_publishing === "not_connected" || !d.instagram_publishing) return "Publishing not connected";
  const p = d.next_instagram_post;
  const paused = d.instagram_publishing === "paused" ? " (publishing paused)" : "";
  if (!p) return `None queued${paused}`;
  const kind = p.kind ? ` · ${String(p.kind).charAt(0)}${String(p.kind).slice(1).toLowerCase()}` : "";
  return `${p.when ? fmtWhen(p.when, true) : "Approved, no time set"}${kind}${paused}`;
}

function TodayWidget({ controls }) {
  const s = useWidgetData("/api/command-center/today");
  const d = s.data;
  return <WidgetFrame id="today" title="Today" link="/crm/calendar" linkLabel="Calendar" state={s} controls={controls}>
    {d && <>
      <p className="text-[11px] uppercase text-zinc-500">Due today · {d.today} (Athens)</p>
      <ItemList items={d.due_today} empty="Nothing scheduled today." />
      <p className="mt-3 text-[11px] uppercase text-zinc-500">Tomorrow</p>
      <ItemList items={d.due_tomorrow} empty="Nothing scheduled tomorrow." />
      <Row label="Overdue" testid="cc-today-overdue" value={d.overdue_count} tone={d.overdue_count ? "text-amber-300" : "text-white"} />
      <Row label="Next X post" testid="cc-today-next-x" value={d.next_x_post ? `${fmtWhen(d.next_x_post.when, true)} · ${d.next_x_post.title}` : "None queued"} />
      <Row label="Next Instagram post" testid="cc-today-next-ig" value={igNext(d)} />
    </>}
  </WidgetFrame>;
}

function MembersWidget({ controls }) {
  const s = useWidgetData("/api/command-center/members");
  const d = s.data;
  return <WidgetFrame id="members" title="Members" link="/crm/members" linkLabel="Members" state={s} controls={controls}>
    {d && (d.total === 0 ? <Empty>No members yet.</Empty> : <>
      <Row label="Total" testid="cc-members-total" value={d.total} />
      <Row label="Free · Basic · VIP" testid="cc-members-tiers" value={`${d.by_tier.free} · ${d.by_tier.basic} · ${d.by_tier.vip}`} />
      <Row label="New sign-ups (7 days)" testid="cc-members-new" value={d.new_last_7_days} tone="text-emerald-300" />
    </>)}
    {d && <>
      <Row label="Model applications (new)" value={d.model_applications_new} tone={d.model_applications_new ? "text-amber-300" : "text-white"} />
      <Row label="Submissions (new)" value={d.submissions_new} />
      <Row label="Open support tickets" testid="cc-members-tickets" value={d.open_tickets} tone={d.open_tickets ? "text-amber-300" : "text-white"} />
      <Row label="VIP requests" value={d.vip_requests === null ? "Not tracked yet" : d.vip_requests} />
    </>}
  </WidgetFrame>;
}

function ContentWidget({ controls }) {
  const s = useWidgetData("/api/command-center/content");
  const d = s.data;
  const muxTotal = d ? Object.values(d.mux_issues || {}).reduce((a, b) => a + b, 0) : 0;
  return <WidgetFrame id="content" title="Content" link="/crm/videos" linkLabel="Videos" state={s} controls={controls}>
    {d && (d.total === 0 ? <Empty>No videos yet.</Empty> : <>
      <ul className="space-y-1">{d.latest.map((v) => <li key={v.id} className="flex justify-between gap-2 text-sm"><span className="truncate text-zinc-200">{v.title}</span><span className={`shrink-0 text-xs ${v.members_visible ? "text-emerald-300" : "text-zinc-500"}`}>{v.members_visible ? "visible" : "hidden"}</span></li>)}</ul>
      <Row label="Drafts" testid="cc-content-drafts" value={d.drafts} />
      <Row label="Hidden from members" value={d.not_visible_to_members} />
      <Row label="Edit queue" value={d.edit_queue} />
      <Row label="Mux issues" testid="cc-content-mux" value={muxTotal ? Object.entries(d.mux_issues).map(([k, v]) => `${v} ${k.replace(/_/g, " ")}`).join(" · ") : "None"} tone={muxTotal ? "text-amber-300" : "text-white"} />
    </>)}
  </WidgetFrame>;
}

function AIWidget({ controls }) {
  const s = useWidgetData("/api/command-center/ai");
  const d = s.data;
  return <WidgetFrame id="ai" title="AI Studio" link="/crm/ai?tab=copilot" linkLabel="Open Copilot" state={s} controls={controls}>
    {d && <>
      {d.briefs_today.length ? <p data-testid="cc-ai-brief" className="text-sm text-zinc-200">{d.brief_summary || `Today's brief ready (${d.briefs_today.join(", ")})`}</p>
        : <Empty>No brief yet today. <Link className="text-[#FF10F0] underline" to="/crm/ai?tab=today">Open today</Link></Empty>}
      <Row label="Awaiting your approval" testid="cc-ai-awaiting" value={d.awaiting_approval_total} tone={d.awaiting_approval_total ? "text-amber-300" : "text-white"} />
      <Row label="Plan proposals · X drafts · campaigns" value={`${d.pending_plan_proposals} · ${d.x_drafts} · ${d.campaign_drafts}`} />
    </>}
  </WidgetFrame>;
}

function HealthWidget({ controls }) {
  const s = useWidgetData("/api/command-center/health");
  const d = s.data;
  return <WidgetFrame id="health" title="Health" link="/crm/settings?tab=connections" linkLabel="Connections" state={s} controls={controls}>
    {d && <>
      <ul className="space-y-1">{d.checks.map((c) => <li key={c.key} data-testid={`cc-health-${c.key}`} className="flex justify-between gap-2 text-sm">
        <span className="text-zinc-200"><span aria-hidden="true" className={c.ok ? "text-emerald-400" : "text-amber-400"}>●</span> {c.label}</span>
        <span className={`text-xs ${c.ok ? "text-emerald-300" : "text-amber-300"}`}>{c.status}{c.last_sync ? ` · ${fmtWhen(c.last_sync, true)}` : ""}</span>
      </li>)}</ul>
      <p className="mt-2 text-[11px] text-zinc-500">{d.ok_count}/{d.total} OK. {d.notes}</p>
    </>}
  </WidgetFrame>;
}

function QuickActionsWidget({ controls }) {
  return <WidgetFrame id="actions" title="Quick actions" controls={controls}>
    <nav aria-label="Admin sections" data-testid="command-center-sections" className="grid grid-cols-2 gap-2">
      {SECTION_LINKS.map((section) => <Link key={section.to} to={section.to} data-testid={`command-center-section-${section.label.toLowerCase().replace(/\s+/g, "-")}`} className="rounded-lg border border-[#FF10F0]/30 bg-black/60 p-3 hover:border-[#FF10F0]"><p className="text-sm font-semibold text-white">{section.label}</p><p className="mt-1 text-[11px] text-zinc-400">{section.detail}</p></Link>)}
    </nav>
  </WidgetFrame>;
}

export const WIDGETS = [
  { id: "revenue", label: "Revenue", Component: RevenueWidget },
  { id: "today", label: "Today", Component: TodayWidget },
  { id: "members", label: "Members", Component: MembersWidget },
  { id: "content", label: "Content", Component: ContentWidget },
  { id: "ai", label: "AI Studio", Component: AIWidget },
  { id: "health", label: "Health", Component: HealthWidget },
  { id: "actions", label: "Quick actions", Component: QuickActionsWidget },
];
const DEFAULT_ORDER = WIDGETS.map((w) => w.id);

export function loadPrefs() {
  try {
    const raw = JSON.parse(localStorage.getItem(PREFS_KEY) || "null");
    const known = new Set(DEFAULT_ORDER);
    const order = Array.isArray(raw?.order) ? raw.order.filter((id) => known.has(id)) : [];
    for (const id of DEFAULT_ORDER) if (!order.includes(id)) order.push(id);
    const hidden = Array.isArray(raw?.hidden) ? raw.hidden.filter((id) => known.has(id)) : [];
    return { order, hidden };
  } catch {
    return { order: DEFAULT_ORDER.slice(), hidden: [] };
  }
}

export default function CommandCenterWidgets() {
  const [prefs, setPrefs] = useState(loadPrefs);
  const [editing, setEditing] = useState(false);
  const save = (next) => {
    setPrefs(next);
    try { localStorage.setItem(PREFS_KEY, JSON.stringify(next)); } catch { /* storage full or blocked */ }
  };
  const move = (id, delta) => {
    const order = prefs.order.slice();
    const i = order.indexOf(id);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= order.length) return;
    [order[i], order[j]] = [order[j], order[i]];
    save({ ...prefs, order });
  };
  const toggle = (id) => save({ ...prefs, hidden: prefs.hidden.includes(id) ? prefs.hidden.filter((x) => x !== id) : [...prefs.hidden, id] });
  const byId = Object.fromEntries(WIDGETS.map((w) => [w.id, w]));
  const visible = prefs.order.filter((id) => !prefs.hidden.includes(id));

  return <div data-testid="cc-widgets" className="space-y-3">
    <div className="flex flex-wrap items-center justify-end gap-2">
      <button type="button" data-testid="cc-customize" onClick={() => setEditing((v) => !v)} className="rounded border border-[#FF10F0]/40 px-3 py-1.5 text-xs text-zinc-200 hover:border-[#FF10F0]">{editing ? "Done" : "Customize widgets"}</button>
      {editing && <button type="button" onClick={() => save({ order: DEFAULT_ORDER.slice(), hidden: [] })} className="rounded border border-zinc-700 px-3 py-1.5 text-xs text-zinc-300">Reset</button>}
    </div>
    {editing && <div data-testid="cc-hidden-list" className="flex flex-wrap gap-2">
      {prefs.order.map((id) => <label key={id} className="inline-flex items-center gap-1 rounded border border-zinc-800 px-2 py-1 text-xs text-zinc-300">
        <input type="checkbox" checked={!prefs.hidden.includes(id)} onChange={() => toggle(id)} aria-label={`Show ${byId[id].label}`} />{byId[id].label}
      </label>)}
    </div>}
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
      {visible.map((id) => {
        const { Component, label } = byId[id];
        const controls = editing ? <>
          <button type="button" aria-label={`Move ${label} up`} onClick={() => move(id, -1)} className="rounded px-1 text-xs text-zinc-400 hover:text-white">↑</button>
          <button type="button" aria-label={`Move ${label} down`} onClick={() => move(id, 1)} className="rounded px-1 text-xs text-zinc-400 hover:text-white">↓</button>
        </> : null;
        return <Component key={id} controls={controls} />;
      })}
    </div>
    {!visible.length && <Empty>All widgets are hidden. Use “Customize widgets” to show them again.</Empty>}
  </div>;
}
